import { z } from "zod";

import { stepUpFields } from "./auth";

/**
 * Kişisel veri talepleri (CLOS-222 `[201]`).
 *
 * ### Kapsam sınırı — kartın kendi cümlesiyle
 *
 * *"Kullanıcı yalnız kendi doğrulanmış hesap kimliği için talep oluşturabilir; başka
 * kullanıcı, tenant, organizasyon, müvekkil veya haricî katılımcı adına export/silme
 * başlatamaz."* Bu yüzden şemalarda `tenantId` YOKTUR — talep hesap düzeyinde, tenant
 * düzeyinde değil (`accountClosure.ts` ile aynı ayrım).
 *
 * Organizasyon/sözleşme export'u CLOS-234, audit export'u CLOS-194'tür; bu şema onlara
 * arka kapı açmaz.
 */

/** İki talep türü. Kapsamları ayrı state machine'lerden yürür (S02/S04). */
export const PRIVACY_REQUEST_TYPES = ["export", "deletion"] as const;
export type PrivacyRequestType = (typeof PRIVACY_REQUEST_TYPES)[number];

/**
 * Talebin durumu — export ve deletion'ın BİRLEŞİMİ.
 *
 * ⚠ Tek alan, iki akış: `requested/processing/ready/failed/expired/cancelled` export'un;
 * `requested/evaluating/retained/anonymizing/completed/failed` deletion'ın kendi
 * geçişleri. Birleştirmek yerine iki ayrı enum açmak da düşünüldü ama ekran (S03) ikisini
 * TEK bir liste olarak gösteriyor ve iki paralel enum'un senkron kalması gerekirdi.
 */
export const PRIVACY_REQUEST_STATUSES = [
  "requested",
  "processing",
  "ready",
  "evaluating",
  "retained",
  "anonymizing",
  "completed",
  "failed",
  "expired",
  "cancelled",
] as const;
export type PrivacyRequestStatus = (typeof PRIVACY_REQUEST_STATUSES)[number];

/**
 * Kullanıcıya gösterilen durum etiketleri (CLOS-1054 — "yalnız renkle olmayan
 * göstergeler"). Ekran BU sözlüğü kullanır; sayfa içinde ikinci bir metin uydurmaz.
 */
export const PRIVACY_REQUEST_STATUS_LABELS: Record<PrivacyRequestStatus, string> = {
  requested: "Talep alındı",
  processing: "Hazırlanıyor",
  ready: "Hazır",
  evaluating: "İnceleniyor",
  retained: "Saklama gereği nedeniyle ertelendi",
  anonymizing: "Anonimleştiriliyor",
  completed: "Tamamlandı",
  failed: "Başarısız",
  expired: "Süresi doldu",
  cancelled: "İptal edildi",
};

/**
 * Talep oluşturma girdisi.
 *
 * ⚠ HASSAS İŞLEM — kartın kriteri: *"açık oturum tek başına yeterli görülmez."* Parola
 * (+ etkinse 2FA) `closeAccountSchema` ile AYNI desen (`stepUpFields`).
 */
export const createPrivacyRequestSchema = z.object({
  type: z.enum(PRIVACY_REQUEST_TYPES, { error: "Talep türü geçersiz" }),
  currentPassword: z
    .string({ error: "Mevcut şifrenizi giriniz" })
    .min(1, "Mevcut şifrenizi giriniz"),
  ...stepUpFields,
});
export type CreatePrivacyRequestInput = z.infer<typeof createPrivacyRequestSchema>;

/** Liste/ekran görünümü — kartın *"talep türü ve kapsadığı veri sınıfları"* kriteri. */
export const privacyRequestViewSchema = z.object({
  id: z.uuid(),
  type: z.enum(PRIVACY_REQUEST_TYPES),
  status: z.enum(PRIVACY_REQUEST_STATUSES),
  dataClasses: z.array(z.string()),
  /** Veri sınıfı bazlı sonuç/gerekçe (CLOS-1054 — "her veri sınıfı için ayrı gösterilir"). */
  decisions: z
    .array(
      z.object({
        dataClass: z.string(),
        outcome: z.enum(["exported", "eligible", "retained", "anonymized", "not_applicable"]),
        reason: z.string().nullable(),
      }),
    )
    .nullable(),
  failureReason: z.string().nullable(),
  requestedAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime().nullable(),
});
export type PrivacyRequestView = z.infer<typeof privacyRequestViewSchema>;

export const privacyRequestListSchema = z.array(privacyRequestViewSchema);
